import { Controller } from "@hotwired/stimulus"
import { apiGet } from "../shared/api"
import { escapeHtml, loadTranslations, t } from "../shared/utils"

export default class extends Controller {
  static targets = ["results", "pagination", "form"]
  static values = { apiUrl: String }

  connect() {
    loadTranslations()
    this.filterTimeout = null
    this.loading = false
    this._fetch(1)
  }

  disconnect() {
    if (this.filterTimeout) clearTimeout(this.filterTimeout)
  }

  onFilterChange() {
    clearTimeout(this.filterTimeout)
    this.filterTimeout = setTimeout(() => this._fetch(1), 200)
  }

  onYearChange() {
    // Year change requires full reload to refresh federation dropdown (federations depend on selected year)
    if (this.hasFormTarget) this.formTarget.submit()
  }

  onSearchInput() {
    const val = (this.formTarget?.querySelector('[name="search"]')?.value || "").trim()
    if (val.length >= 3 || val.length === 0) {
      clearTimeout(this.filterTimeout)
      this.filterTimeout = setTimeout(() => this._fetch(1), 350)
    }
  }

  async _fetch(page) {
    if (this.loading) return
    this.loading = true

    if (this.hasResultsTarget) {
      this.resultsTarget.style.opacity = "0.5"
      this.resultsTarget.style.pointerEvents = "none"
    }

    try {
      const params = this._buildParams(page)
      const json = await apiGet(this.apiUrlValue, params)
      const { data, meta } = json

      if (this.hasResultsTarget) {
        this.resultsTarget.innerHTML = data.length > 0
          ? this._renderTable(data, meta)
          : this._renderEmpty()
      }

      if (this.hasPaginationTarget) {
        this.paginationTarget.innerHTML = meta.total_pages > 1 ? this._renderPagination(meta, params) : ""
      }
    } catch (error) {
      console.error("Error fetching tournaments:", error)
      if (this.hasResultsTarget) {
        this.resultsTarget.innerHTML = `<div class="text-center py-5 text-danger">${escapeHtml(t("common.error") || "Błąd")}</div>`
      }
    } finally {
      this.loading = false
      if (this.hasResultsTarget) {
        this.resultsTarget.style.opacity = ""
        this.resultsTarget.style.pointerEvents = ""
      }
    }
  }

  _buildParams(page) {
    const params = { page }
    if (!this.hasFormTarget) return params

    const formData = new FormData(this.formTarget)
    for (const [key, value] of formData.entries()) {
      if (value && key !== "page") params[key] = value
    }
    if (params.search && params.search.trim().length < 3) delete params.search
    return params
  }

  _renderTable(data, meta) {
    const offset = (meta.current_page - 1) * meta.per_page
    const rows = data.map((t, idx) => this._renderRow(t, offset + idx + 1))
    const lp = escapeHtml(t("tournaments.table.lp") || "Lp.")
    const date = escapeHtml(t("tournaments.table.date") || "Data")
    const name = escapeHtml(t("tournaments.table.name") || "Nazwa")
    const venue = escapeHtml(t("tournaments.table.venue") || "Miejsce")
    const organizer = escapeHtml(t("tournaments.table.organizer") || "Organizator")
    const region = escapeHtml(t("tournaments.table.region") || "Okręg")
    const www = escapeHtml(t("tournaments.table.www") || "WWW")

    return `
      <table class="table table-sm table-striped table-hover tournaments-table">
        <thead class="table-light">
          <tr>
            <th class="text-end" style="width: 3em;">${lp}</th>
            <th style="width: 9em;">${date}</th>
            <th>${name}</th>
            <th>${venue}</th>
            <th>${organizer}</th>
            <th>${region}</th>
            <th style="width: 4em;">${www}</th>
          </tr>
        </thead>
        <tbody>${rows.join("")}</tbody>
      </table>`
  }

  _renderRow(t, idx) {
    const dateStr = t.start_date ? new Date(t.start_date).toLocaleDateString("pl-PL", { day: "2-digit", month: "2-digit", year: "numeric" }) : "-"
    const nameCell = t.has_blocks
      ? `<a href="/tournaments/${escapeHtml(t.slug)}">${escapeHtml(t.name)}</a>`
      : t.source_url
        ? `<a href="${escapeHtml(t.source_url)}" target="_blank" rel="noopener noreferrer">${escapeHtml(t.name)}</a>`
        : escapeHtml(t.name)
    const venue = escapeHtml(t.venue || "-")
    const organizer = escapeHtml(t.organizer_name || "-")
    const region = escapeHtml(t.region || "-")
    const wwwCell = t.source_url
      ? `<a class="btn btn-sm btn-outline-secondary" href="${escapeHtml(t.source_url)}" target="_blank" rel="noopener noreferrer"><i class="fa-solid fa-arrow-up-right-from-square"></i></a>`
      : '<span class="text-muted">-</span>'
    const rowClass = t.row_css_class ? ` class="${escapeHtml(t.row_css_class)}"` : ""

    return `<tr${rowClass}>
      <td class="text-end">${idx}</td>
      <td class="text-nowrap">${dateStr}</td>
      <td>${nameCell}</td>
      <td>${venue}</td>
      <td>${organizer}</td>
      <td>${region}</td>
      <td>${wwwCell}</td>
    </tr>`
  }

  _renderEmpty() {
    const msg = escapeHtml(t("tournaments.no_tournaments") || "Brak turniejów")
    const hint = escapeHtml(t("tournaments.try_different_year") || "Spróbuj wybrać inny rok (np. 2024, 2025).")
    return `<div class="text-center py-5"><p class="text-muted">${msg}</p><p class="text-muted small">${hint}</p></div>`
  }

  _renderPagination(meta) {
    const { current_page, total_pages } = meta
    const prev = current_page > 1 ? current_page - 1 : null
    const next = current_page < total_pages ? current_page + 1 : null

    let html = '<ul class="default-pagination lab-ul justify-content-center">'

    html += prev
      ? `<li><a href="#" data-action="click->tournaments#goToPage" data-page="${prev}"><i class="fa-solid fa-angle-left"></i></a></li>`
      : '<li><span class="text-muted"><i class="fa-solid fa-angle-left"></i></span></li>'

    const start = Math.max(1, current_page - 2)
    const end = Math.min(total_pages, current_page + 2)

    for (let i = start; i <= end; i++) {
      const active = i === current_page ? ' class="active"' : ""
      html += `<li><a href="#" data-action="click->tournaments#goToPage" data-page="${i}"${active}>${String(i).padStart(2, "0")}</a></li>`
    }

    html += next
      ? `<li><a href="#" data-action="click->tournaments#goToPage" data-page="${next}"><i class="fa-solid fa-angle-right"></i></a></li>`
      : '<li><span class="text-muted"><i class="fa-solid fa-angle-right"></i></span></li>'

    html += "</ul>"
    return html
  }

  goToPage(event) {
    event.preventDefault()
    const page = event.currentTarget.dataset.page
    if (page) this._fetch(parseInt(page, 10))
  }
}
